import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { createTheme } from '@mui/material/styles';
import theme from '../../../theme';
import { useMenu99PricingDetails, useMenuBundlePricingDetails } from '../../../services/dashboard/dashboard.service';
import {
  BuildRequestBodyParams,
  IndexableDataStructureWrapper,
} from '../../../components/MenuTreeView/MenuTreeView.types';
import useTreeViewRowStates from '../../../components/MenuTreeView/useMenuTreeViewRowStates';
import { Organization } from '../../../services/menu/menu.types';

interface UsePricingMetricsParams {
  from: Dayjs | null | undefined;
  to: Dayjs | null | undefined;
}

interface ExportColumnDef {
  label: string;
  key: string;
}

export const usePricingMetrics = ({ from, to }: UsePricingMetricsParams) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [activePricingTab, setActivePricingTab] = useState<'99pricing' | 'bundlepricing'>('99pricing');
  // @ts-ignore - Ignoring theme type issues
  const muiTheme = createTheme(theme);
  // Format date for the CSV filename
  const formattedDate = from ? dayjs(from).format('MMMM D, YYYY') : '';

  // Handle the drawer state
  const toggleDrawer = (newOpen: boolean) => {
    setOpenDrawer(newOpen);
  };
  // Define drawer and set the active tab
  const defineDrawer = (tab: '99pricing' | 'bundlepricing' = '99pricing') => {
    setActivePricingTab(tab);
    setOpenDrawer(true);
  };  // Prepare request parameters
  const requestParams = {
    from: from?.toISOString(),
    to: to?.get('minutes') === 55 ? to?.set('minutes', 59).toISOString() : to?.toISOString(),
  }; // Fetch 99 pricing details from API when drawer is open
  const { data: organizations, isLoading } = useMenu99PricingDetails(requestParams, openDrawer);
  const availableOrganizations = organizations?.data || [];
  
  // Fetch bundle pricing details from API when component loads (regardless of drawer state)
  const { data: bundleOrganizations, isLoading: isBundleLoading } = useMenuBundlePricingDetails(requestParams, openDrawer);

  // Make sure all rows have valid types that exist in our bundleDataStructure
  const availableBundleOrganizations = (bundleOrganizations?.data || []).map((org: any) => {
    // Make sure the organization has the correct type
    if (!org.type) org.type = 'org';

    // Process children to ensure they have the right type
    if (org.children && Array.isArray(org.children)) {
      org.children = org.children.map((store: any) => {
        if (!store.type) store.type = 'store';
        return store;
      });
    }
  

    return org;
  }); // Function to build labels for the TreeView
  // Note: In the component, this uses HeaderWrapper, but we avoid JSX in the hook
  const buildLabel = (params: BuildRequestBodyParams): any => {
    return params.row.label;
  };

  // Define the data structure for the 99 Pricing TreeView
  const dataStructure: IndexableDataStructureWrapper = {
    org: {
      idKey: 'org',
      showCheckboxes: false,
      buildLabel: buildLabel,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return { orgNo: params.row.id, activateOrExtract: undefined };
      },
      buildParentArray: () => [],
      getIndexByType: undefined,
    },
    store: {
      idKey: 'store',
      showCheckboxes: false,
      buildLabel: buildLabel,
      buildParentArray: () => [],
      getIndexByType: undefined,
    },
    menu: {
      to: (params: BuildRequestBodyParams) => `/menus/${params.row.id}`,
      target: '_blank',
      isLink: true,
      idKey: 'menu',
      showCheckboxes: false,
      buildLabel: buildLabel,
      buildParentArray: () => [],
      getIndexByType: undefined,
    },
    menuItem: {
      to: () => '',
      isLink: false,
      idKey: 'menuItem',
      showCheckboxes: false,
      buildLabel: buildLabel,
      buildParentArray: undefined, // the last element can't be expanded
      getIndexByType: undefined,
    },
  };  // Define the data structure for the Bundle Pricing TreeView (Org > Store > 99bundleItem)
  const bundleDataStructure: IndexableDataStructureWrapper = {
    org: {
      idKey: 'org',
      showCheckboxes: false,
      buildLabel: (params: BuildRequestBodyParams) => params.row.label,
      buildParentArray: (params: Organization) => {
        return [{ level: 0, id: params.id }];
      },
      getIndexByType: () => {
        return 0;
      },
    },
    store: {
      idKey: 'store',
      showCheckboxes: false,
      isLink: false,
      buildLabel: (params: BuildRequestBodyParams) => params.row.label,
      buildParentArray: (params: Organization) => {
        const parents = [];
        if (params.parents) {
          parents.push({ level: 0, id: params.parents['org'] });
        }
        parents.push({ level: 1, id: params.id });
        return parents;
      },
      getIndexByType: undefined,
    },
    '99bundleItem': {
      to: () => '',
      idKey: '99bundleItem',
      showCheckboxes: false,
      isLink: false,
      buildLabel: (params: BuildRequestBodyParams) => params.row.label,
      buildParentArray: undefined, // the last element can't be expanded
      getIndexByType: undefined,
    },


  };

  // Set up the TreeView row states for 99 pricing
  const { expandedRows, handleRowClick } = useTreeViewRowStates(
    dataStructure,
    'selectMenus99Pricing' + undefined,
    true
  );  // Set up the TreeView row states for bundle pricing
  // Create a custom handler that prevents any action when clicking on store nodes
  const { expandedRows: bundleExpandedRows, handleRowClick: originalBundleRowClick } = useTreeViewRowStates(
    bundleDataStructure,
    'selectMenusBundlePricing' + undefined,
    true
  );
  
  // Custom handler to prevent clicking on leaf nodes
  const handleBundleRowClick = (row: any, rowExpanded: boolean) => {
    // Only allow clicking on org and store nodes (which have children)
    if (row.type === 'org' || row.type === 'store') {
      // For store type, check if it has non-empty children before expanding
      if (row.type === 'store' && (!row.children || row.children.length === 0)) {
        return; // Don't expand empty store nodes
      }
      originalBundleRowClick(row, rowExpanded);
    }
    // For 99bundleItem type, do nothing (it's the final level)
  };
  // We use an empty noRowsMessage in PricingMetrics.tsx to prevent 
  // showing "No records found" for bundle items
  // Define column definitions for 99 Pricing CSV export
  const columnDefs: ExportColumnDef[] = [
    { label: 'Organization', key: 'orgName' },
    { label: 'Organization No.', key: 'orgId' },
    { label: 'Store', key: 'storeName' },
    { label: 'Store No.', key: 'storeId' },
    { label: 'Menu', key: 'menuName' },
    { label: 'Menu No.', key: 'menuId' },
    { label: 'Menu Item', key: 'menuItemName' },
    { label: 'Menu Item No.', key: 'menuItemId' },
  ];
  // Define column definitions for Bundle Pricing CSV export
  const bundleColumnDefs: ExportColumnDef[] = [
    { label: 'Organization', key: 'orgName' },
    { label: 'Organization No.', key: 'orgId' },
    { label: 'Store', key: 'storeName' },
    { label: 'Store No.', key: 'storeId' },
    { label: 'Bundle Item', key: 'bundleItemName' }
  ];

  // Process the TreeView data for 99 Pricing CSV export
  const extractDataFromPayload = (payload: any[]) => {
    const flatData: any[] = [];

    if (!payload || !Array.isArray(payload)) {
      return flatData;
    }

    payload.forEach((org) => {
      if (!org.children || !Array.isArray(org.children)) {
        flatData.push({
          orgName: org.label,
          orgId: org.id,
          storeName: '',
          storeId: '',
          menuName: '',
          menuId: '',
          menuItemName: '',
          menuItemId: '',
        });
        return;
      }

      org.children.forEach((store: any) => {
        if (!store.children || store.children.length === 0) {
          flatData.push({
            orgName: org.label,
            orgId: org.id,
            storeName: store.label,
            storeId: store.id,
            menuName: '',
            menuId: '',
            menuItemName: '',
            menuItemId: '',
          });
          return;
        }

        store.children.forEach((menu: any) => {
          if (!menu.children || menu.children.length === 0) {
            flatData.push({
              orgName: org.label,
              orgId: org.id,
              storeName: store.label,
              storeId: store.id,
              menuName: menu.label,
              menuId: menu.id,
              menuItemName: '',
              menuItemId: '',
            });
            return;
          }

          menu.children.forEach((menuItem: any) => {
            flatData.push({
              orgName: org.label,
              orgId: org.id,
              storeName: store.label,
              storeId: store.id,
              menuName: menu.label,
              menuId: menu.id,
              menuItemName: menuItem.label,
              menuItemId: menuItem.id,
            });
          });
        });
      });
    });

    return flatData;
  };
  // Process the TreeView data for Bundle Pricing CSV export
  const extractBundleDataFromPayload = (payload: any[]) => {
    const flatData: any[] = [];

    if (!payload || !Array.isArray(payload)) {
      return flatData;
    }

    payload.forEach((org) => {
      if (!org.children || !Array.isArray(org.children)) {
        flatData.push({
          orgName: org.label,
          orgId: org.id,
          storeName: '',
          storeId: '',
          bundleItemName: '',
          bundleItemId: '',
        });
        return;
      }

      org.children.forEach((store: any) => {
        if (!store.children || store.children.length === 0) {
          flatData.push({
            orgName: org.label,
            orgId: org.id,
            storeName: store.label,
            storeId: store.id,
            bundleItemName: '',
            bundleItemId: '',
          });
          return;
        }
        
        store.children.forEach((bundleItem: any) => {
          flatData.push({
            orgName: org.label,
            orgId: org.id,
            storeName: store.label,
            storeId: store.id,
            bundleItemName: bundleItem.label,
            bundleItemId: bundleItem.id,
          });
        });
      });
    });

    return flatData;
  };
  return {
    openDrawer,
    toggleDrawer,
    defineDrawer,
    muiTheme,
    isLoading,
    isBundleLoading,
    availableOrganizations,
    availableBundleOrganizations,
    dataStructure,
    bundleDataStructure,
    expandedRows,
    bundleExpandedRows,
    handleRowClick,
    handleBundleRowClick,
    columnDefs,
    bundleColumnDefs,
    extractDataFromPayload,
    extractBundleDataFromPayload,    formattedDate,
    activePricingTab,
  };
};
